import * as ReactNamespace from 'react'
import type { ComponentType, ReactNode } from 'react'

import type { SandboxTemplateEntry } from '../sandbox-protocol'

interface TemplateRegistryEntry {
  code: string
  defaultValues: Record<string, unknown>
}

interface TemplateCompileResult {
  Component: ComponentType<Record<string, unknown>> | null
  error: string | null
}

type TemplateCompiler = (code: string) => TemplateCompileResult

interface TemplateRuntimeState {
  registry: Map<string, TemplateRegistryEntry>
  componentCache: Map<string, TemplateCompileResult>
  compiler: TemplateCompiler | null
}

interface HotImportMeta extends ImportMeta {
  hot?: {
    data: Record<string, unknown>
  }
}

const hotData = (import.meta as HotImportMeta).hot?.data
const runtimeState = (hotData?.templateRuntimeState as TemplateRuntimeState | undefined) ?? {
  registry: new Map<string, TemplateRegistryEntry>(),
  componentCache: new Map<string, TemplateCompileResult>(),
  compiler: null
}

if (hotData) {
  hotData.templateRuntimeState = runtimeState
}

export function setTemplateCompiler(compiler: TemplateCompiler): void {
  if (runtimeState.compiler !== compiler) {
    runtimeState.componentCache.clear()
  }
  runtimeState.compiler = compiler
}

export function setTemplateRegistry(entries: readonly SandboxTemplateEntry[]): void {
  runtimeState.registry.clear()
  for (const entry of entries) {
    const path = normalizeTemplatePath(entry.path)
    if (path === '') continue
    runtimeState.registry.set(path, {
      code: entry.code,
      defaultValues: entry.defaultValues
    })
  }
}

function normalizeTemplatePath(path: string): string {
  return path.trim().replace(/^\/+|\/+$/g, '')
}

export function Template(props: { path?: string } & Record<string, unknown>): ReactNode {
  const { path, ...overrides } = props
  if (typeof path !== 'string' || path.trim() === '') {
    return ReactNamespace.createElement(TemplateInlineError, {
      message: '<Template /> requires a non-empty `path` prop.'
    })
  }

  const normalizedPath = normalizeTemplatePath(path)
  const entry = runtimeState.registry.get(normalizedPath)
  if (!entry) {
    return ReactNamespace.createElement(TemplateInlineError, {
      message: `No template found at "${normalizedPath}".`
    })
  }

  if (runtimeState.compiler === null) {
    return null
  }

  let compiled = runtimeState.componentCache.get(entry.code)
  if (!compiled) {
    compiled = runtimeState.compiler(entry.code)
    runtimeState.componentCache.set(entry.code, compiled)
  }
  if (compiled.Component === null) {
    return ReactNamespace.createElement(TemplateInlineError, {
      message: `Template "${normalizedPath}" failed to compile: ${compiled.error ?? 'unknown error'}`
    })
  }

  const merged: Record<string, unknown> = { ...entry.defaultValues, ...overrides }
  return ReactNamespace.createElement(compiled.Component, merged)
}

function TemplateInlineError({ message }: { message: string }): ReactNode {
  return ReactNamespace.createElement(
    'div',
    {
      className: 'rounded-md border border-st-down/40 bg-st-down-bg px-3 py-2 text-xs text-st-down'
    },
    message
  )
}
